import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import '../styles/addToLeagueModal.css';

export default function AddToLeagueModal({ isOpen, onClose, onSuccess, teamId }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [leagues, setLeagues] = useState([]);
    const [seasons, setSeasons] = useState([]);
    
    const [selectedLeague, setSelectedLeague] = useState(null);
    const [selectedSeason, setSelectedSeason] = useState(null);
    
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadLeagues();
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedLeague) {
            loadSeasons(selectedLeague);
        } else {
            setSeasons([]);
            setSelectedSeason(null);
        }
    }, [selectedLeague]);

    const loadLeagues = async () => {
        try {
            setLoading(true);
            if (checkIfTokenExpired()) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();
            // Используем endpoint который возвращает лиги с сезонами
            const response = await axios.post(
                BASE_URL.url + '/leagues/1/adminLeagues',
                {},
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            console.log('Leagues response:', response.data);
            
            // Данные приходят напрямую как массив
            if (Array.isArray(response.data)) {
                console.log('Leagues data:', response.data);
                setLeagues(response.data);
            } else if (response.data && response.data.data) {
                console.log('Leagues data:', response.data.data);
                setLeagues(response.data.data);
            }
        } catch (err) {
            console.error('Error loading leagues:', err);
            setError('Ошибка загрузки лиг');
        } finally {
            setLoading(false);
        }
    };

    const loadSeasons = async (leagueId) => {
        try {
            setLoading(true);
            
            // Ищем выбранную лигу и получаем её сезоны
            const selectedLeagueData = leagues.find(l => l.league_id == leagueId);
            console.log('Selected league:', selectedLeagueData);
            
            if (selectedLeagueData && selectedLeagueData.seasons && selectedLeagueData.seasons.length > 0) {
                console.log('Using seasons from league data:', selectedLeagueData.seasons);
                setSeasons(selectedLeagueData.seasons);
            } else {
                console.log('No seasons found in league data');
                setSeasons([]);
                setError('У этой лиги нет доступных сезонов');
            }
        } catch (err) {
            console.error('Error loading seasons:', err);
            setSeasons([]);
            setError('Ошибка загрузки сезонов');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedLeague || !selectedSeason) {
            setError('Выберите лигу и сезон');
            return;
        }

        try {
            setSaving(true);
            setError(null);
            
            console.log('Connecting team to league:', {
                team_id: teamId,
                league_id: selectedLeague,
                season_id: selectedSeason
            });
            
            if (checkIfTokenExpired()) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();
            
            // Формат данных: оба массива, один заполнен, другой пустой
            const requestData = {
                add_data: [{
                    league_id: parseInt(selectedLeague),
                    season_id: parseInt(selectedSeason)
                }],
                remove_data: [],
                teamId: parseInt(teamId)
            };
            
            console.log('Request data:', requestData);
            console.log('URL:', BASE_URL.url + API_URLS.connectToLeague);
            
            const response = await axios.post(
                BASE_URL.url + API_URLS.connectToLeague,
                requestData,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    timeout: 30000,
                    validateStatus: function (status) {
                        return status < 500;
                    }
                }
            );
            
            console.log('Response status:', response.status);
            console.log('Response data:', response.data);
            
            console.log('Response status:', response.status);
            console.log('Response data:', response.data);
            
            if (response.status >= 400) {
                throw new Error(`Server returned status ${response.status}: ${JSON.stringify(response.data)}`);
            }
            
            console.log('Connected successfully:', response.data);
            alert('Команда успешно добавлена в лигу!');
            onSuccess();
            handleClose();
        } catch (err) {
            console.error('Error connecting to league:', err);
            console.error('Error response:', err.response?.data);
            console.error('Error status:', err.response?.status);
            console.error('Error message:', err.message);
            console.error('Full error:', JSON.stringify(err, null, 2));
            
            let errorMessage = 'Ошибка при добавлении команды в лигу';
            
            if (err.message === 'Network Error') {
                errorMessage = 'Ошибка сети. Проверьте подключение к серверу.';
            } else if (err.code === 'ECONNABORTED') {
                errorMessage = 'Превышено время ожидания ответа сервера';
            } else if (err.response) {
                errorMessage = err.response?.data?.message 
                    || err.response?.data?.error 
                    || `Ошибка сервера: ${err.response.status}`;
            } else {
                errorMessage = err.message || errorMessage;
            }
            
            setError(errorMessage);
            alert('Ошибка: ' + errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setSelectedLeague(null);
        setSelectedSeason(null);
        setError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modalOverlay">
            <div className="modalContent addToLeagueModal">
                <div className="modalHeader">
                    <h2>Добавить команду в лигу</h2>
                    <button className="modalCloseButton" onClick={handleClose}>
                        <FiX />
                    </button>
                </div>

                <div className="modalBody">
                    {error && (
                        <div className="errorMessage" style={{marginBottom: '16px'}}>
                            {error}
                        </div>
                    )}

                    <div className="formGroup">
                        <label>Лига</label>
                        <select
                            value={selectedLeague || ''}
                            onChange={(e) => setSelectedLeague(e.target.value)}
                            disabled={loading || leagues.length === 0}
                        >
                            <option value="">Выберите лигу</option>
                            {leagues.map(league => (
                                <option key={league.league_id} value={league.league_id}>
                                    {league.league_name} ({league.championat_name})
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedLeague && (
                        <div className="formGroup">
                            <label>Сезон</label>
                            <select
                                value={selectedSeason || ''}
                                onChange={(e) => setSelectedSeason(e.target.value)}
                                disabled={loading || seasons.length === 0}
                            >
                                <option value="">Выберите сезон</option>
                                {seasons.map(season => (
                                    <option key={season.season_id} value={season.season_id}>
                                        {season.season_name || `Сезон ${season.season_id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="modalFooter">
                    <button 
                        className="cancelButton" 
                        onClick={handleClose}
                        disabled={saving}
                    >
                        Отмена
                    </button>
                    <button 
                        className="saveButton" 
                        onClick={handleSave}
                        disabled={!selectedLeague || !selectedSeason || saving}
                    >
                        {saving ? 'Сохранение...' : 'Добавить'}
                    </button>
                </div>
            </div>
        </div>
    );
}
